import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"

type Theme = "light" | "dark"

const STORAGE_KEY = "bubbledesk-platform-theme-v2"
const THEME_COLOR = { light: "#f4f6f8", dark: "#0b0d10" } as const

function storedTheme(): Theme | null {
  const value = localStorage.getItem(STORAGE_KEY)
  return value === "light" || value === "dark" ? value : null
}

const ThemeContext = createContext<{ theme: Theme; setTheme: (theme: Theme) => void } | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => storedTheme() ?? "dark")

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark")
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme])
  }, [theme])

  useEffect(() => {
    if (!storedTheme()) localStorage.setItem(STORAGE_KEY, "dark")
  }, [])

  const setTheme = (next: Theme) => {
    localStorage.setItem(STORAGE_KEY, next)
    setThemeState(next)
  }

  const value = useMemo(() => ({ theme, setTheme }), [theme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) throw new Error("useTheme must be used within ThemeProvider")
  return context
}
